import { FC } from "react";
import { AlertTriangle, Building, Check } from "lucide-react";
import { CopropertyDto } from "api/coproperty";
import { buildingStandingLabel } from "./CopropertyFieldControls";

/** État d'une ligne de la checklist d'éligibilité du dossier. */
export type RecapChecklistStatus = "done" | "active" | "todo" | "blocked";

export interface RecapChecklistItem {
  label: string;
  status: RecapChecklistStatus;
}

/** Surface en m² au format français ("4 250 m²"). */
const formatArea = (value: number): string => `${value.toLocaleString("fr-FR")} m²`;

/** Montant en euros sans décimales ("8 500 000 €"). */
const formatAmount = (value: number): string =>
  value.toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

/** Libellé des natures cochées de la copropriété ("Habitation, commerces"). */
const naturesLabel = (coproperty: CopropertyDto): string => {
  const natures = [
    coproperty.natureHousing && "Habitation",
    coproperty.natureOffices && "Bureaux",
    coproperty.natureShops && "Commerces",
    coproperty.natureParking && "Parking",
  ].filter((n): n is string => Boolean(n));

  if (natures.length === 0) return "—";
  return natures
    .map((n, index) => (index === 0 ? n : n.toLowerCase()))
    .join(", ");
};

const RecapRow: FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="flex justify-between gap-2">
    <span className="text-[13px] text-gray-500">{label}</span>
    <span className={`text-right text-[13px] font-semibold ${highlight ? "text-primary" : "text-gray-700"}`}>
      {value}
    </span>
  </div>
);

const ChecklistRow: FC<RecapChecklistItem> = ({ label, status }) => (
  <div className="flex items-center gap-2">
    {status === "done" && <Check className="h-4 w-4 flex-none text-green-700" />}
    {status === "active" && <span className="h-[15px] w-[15px] flex-none rounded-full border-2 border-primary" />}
    {status === "todo" && <span className="h-[15px] w-[15px] flex-none rounded-full border-2 border-gray-300" />}
    {status === "blocked" && <AlertTriangle className="h-4 w-4 flex-none text-red-700" />}
    <span
      className={`text-[13px] ${
        status === "active"
          ? "font-semibold text-zinc-800"
          : status === "todo"
            ? "text-gray-400"
            : status === "blocked"
              ? "font-semibold text-red-700"
              : "text-gray-700"
      }`}
    >
      {label}
    </span>
  </div>
);

interface CopropertyRecapPanelProps {
  coproperty: CopropertyDto;
  /** Masque la fiche (déjà portée par la carte d'identité) et ne garde que l'avancement. */
  hideIdentity?: boolean;
  /** Avancement des sous-étapes du dossier, affiché sous la fiche. */
  checklist: RecapChecklistItem[];
  /**
   * Variante « surface retenue » : Σ des surfaces des bâtiments et SMP à la place du
   * terrain (utilisée par la boucle bâtiments et la synthèse).
   */
  showRetainedArea?: boolean;
}

/**
 * Colonne récapitulative de l'étape copropriété (MRI) : la fiche de la copropriété et
 * l'avancement de l'éligibilité du dossier, toujours visibles pendant le questionnaire
 * et la boucle bâtiments.
 */
export const CopropertyRecapPanel: FC<CopropertyRecapPanelProps> = ({
  coproperty,
  checklist,
  showRetainedArea = false,
  hideIdentity = false,
}) => {
  const buildingsAreaSum = (coproperty.buildings ?? []).reduce(
    (sum, building) => sum + (building.area ?? 0),
    0
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border bg-white p-4">
        {!hideIdentity && (
        <div className="mb-3 flex items-center gap-2.5">
          <Building className="h-[18px] w-[18px] flex-none text-primary" />
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-zinc-800">{coproperty.name || "Copropriété"}</p>
            {coproperty.registrationNumber && (
              <p className="text-xs text-gray-500">Immatriculation {coproperty.registrationNumber}</p>
            )}
          </div>
        </div>
        )}
        <div className={`flex flex-col gap-2 ${hideIdentity ? "" : "border-t border-gray-100 pt-3"}`}>
          <RecapRow
            label="Surface déclarée"
            value={coproperty.area != null ? formatArea(coproperty.area) : "—"}
          />
          {showRetainedArea ? (
            <>
              <RecapRow
                label="Σ surfaces bâtiments"
                value={buildingsAreaSum > 0 ? formatArea(buildingsAreaSum) : "—"}
              />
              <RecapRow
                label="Surface retenue"
                value={coproperty.retainedArea != null ? formatArea(coproperty.retainedArea) : "—"}
              />
              <RecapRow label="Standing de l'immeuble" value={buildingStandingLabel(coproperty.buildingStanding) ?? "—"} />
              <RecapRow
                label="SMP - Sinistre maximum possible"
                value={coproperty.smp != null ? formatAmount(coproperty.smp) : "—"}
                highlight
              />
            </>
          ) : (
            <>
              <RecapRow
                label="Espace vert"
                value={coproperty.greenSpaceArea != null ? formatArea(coproperty.greenSpaceArea) : "—"}
              />
              <RecapRow
                label="Altitude > 1 500 m"
                value={[
                  coproperty.above1500mAltitude == null ? "—" : coproperty.above1500mAltitude ? "Oui" : "Non",
                  coproperty.altitudeMeters != null && `(${Math.round(coproperty.altitudeMeters)} m)`,
                ]
                  .filter(Boolean)
                  .join(" ")}
              />
              <RecapRow label="Natures" value={naturesLabel(coproperty)} />
            </>
          )}
        </div>
        {showRetainedArea && (
          <p className="mt-2.5 text-xs text-gray-400">
            La surface retenue est le maximum entre la surface déclarée et la somme des surfaces des
            bâtiments. Le SMP vaut la surface retenue × la valeur de reconstruction du standing de l'immeuble.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2.5 rounded-xl border bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Éligibilité du dossier
        </p>
        {checklist.map((item) => (
          <ChecklistRow key={item.label} {...item} />
        ))}
      </div>
    </div>
  );
};
